/**
 * Workspace Agent Config API Client
 * Code execution prompts, quality gate commands, verify checks, AI prompt templates.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const WORKSPACES_API_BASE = '/api/workspaces';

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(endpoint, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options.headers },
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(await response.text() || response.statusText);
  }
  if (response.status === 204) return null as T;
  return response.json();
}

// ─── Types ──────────────────────────────────────────────────────

export interface AgentConfig {
  codeExecution: {
    systemPromptPersona: string;
    projectContext: string;
    developmentStandards: string;
    executionInstructions: string;
    gitBranchingTemplate: string;
  };
  defaults: {
    requiredCredentials: string[];
    qualityGateCommands: Record<string, string>;
    defaultVerifyChecks: string[];
  };
  prompts: {
    contentAnalyzer: string;
    readinessResolver: string;
    visualQA: string;
    documentAnalysis: string;
    epicGeneration: string;
    storyGeneration: string;
    agentTaskTemplate: string;
    chatSystemPrompt: string;
  };
  spriteInstructions: {
    claudeMdTemplate: string;
    additionalInstructions: string;
    codeConventions: string[];
    commonFailures: string[];
  };
}

export type AgentConfigUpdate = {
  codeExecution?: Partial<AgentConfig['codeExecution']>;
  defaults?: Partial<AgentConfig['defaults']>;
  prompts?: Partial<AgentConfig['prompts']>;
  spriteInstructions?: Partial<AgentConfig['spriteInstructions']>;
};

// ─── API Functions ──────────────────────────────────────────────

export async function getAgentConfig(workspaceId: string): Promise<AgentConfig> {
  return request<AgentConfig>(`${WORKSPACES_API_BASE}/${workspaceId}/agent-config`);
}

export async function updateAgentConfig(
  workspaceId: string,
  config: AgentConfigUpdate,
): Promise<AgentConfig> {
  return request<AgentConfig>(`${WORKSPACES_API_BASE}/${workspaceId}/agent-config`, {
    method: 'PATCH',
    body: JSON.stringify(config),
  });
}

// ─── Hooks ──────────────────────────────────────────────────────

export function useAgentConfig(workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['workspace-agent-config', workspaceId],
    queryFn: () => getAgentConfig(workspaceId!),
    enabled: !!workspaceId,
    staleTime: 60000,
  });
}

export function useUpdateAgentConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, config }: { workspaceId: string; config: AgentConfigUpdate }) =>
      updateAgentConfig(workspaceId, config),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workspace-agent-config', variables.workspaceId] });
    },
  });
}
